/**
 * WebSocket Context
 * Manages a singleton WebSocket connection that persists across the app
 * Only reconnects when switching between conversations
 */

import React, { createContext, useContext, useCallback, useState, useEffect, useRef } from 'react';
import { getWebSocketManager } from '../utils/websocket';
import { Conversation } from '../types/conversations';

interface WebSocketContextValue {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  insuranceReportId: string | null;
  setInsuranceReportId: (id: string | null) => void;
  getCurrentConversationId: () => string | undefined;
  connectToConversation: (conversationId: string, insuranceReportId?: string) => Promise<void>;
  disconnect: () => void;
  sendMessage: (message: string, conversationId: string, attachmentIds?: string[]) => void;
  onMessage: (handler: (data: unknown) => void) => () => void;
  onTitleUpdate: (callback: (data: { conversationId: string; title: string }) => void) => () => void;
  onConversationCreated: (callback: (data: { conversation: Conversation }) => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insuranceReportId, setInsuranceReportId] = useState<string | null>(null);

  const wsManagerRef = useRef(getWebSocketManager());
  const currentConversationIdRef = useRef<string>();
  const isConnectingRef = useRef(false);

  // Handle WebSocket connection status changes
  const handleConnectionChange = useCallback((connected: boolean) => {
    console.log('WebSocket connection change handler called:', connected);
    setIsConnected(connected);
    setIsConnecting(false);
    isConnectingRef.current = false;
    
    if (connected) {
      setError(null);
      console.log('WebSocket connected in context');
      // Double-check that the manager agrees with the connected state
      const managerConnected = wsManagerRef.current.isConnected();
      console.log('Manager isConnected check:', managerConnected);
    } else {
      console.log('WebSocket disconnected in context');
    }
  }, []);

  // Connect to a specific conversation
  const connectToConversation = useCallback(async (conversationId: string, insuranceReportId?: string) => {
    const wsManager = wsManagerRef.current;

    // If already connecting to the same conversation, return
    if (isConnectingRef.current && currentConversationIdRef.current === conversationId) {
      return;
    }

    // If already connected to the same conversation, return
    if (wsManager.isConnected() && currentConversationIdRef.current === conversationId) {
      return;
    }

    // If connected to a different conversation, disconnect first
    if (wsManager.isConnected() && currentConversationIdRef.current !== conversationId) {
      console.log(`Switching from conversation ${currentConversationIdRef.current} to ${conversationId}`);
      wsManager.disconnect();
    }

    isConnectingRef.current = true;
    currentConversationIdRef.current = conversationId;
    setIsConnecting(true);
    setError(null);

    // Set initial insuranceReportId from location.state (will be overridden by metadata if exists)
    if (insuranceReportId) {
      setInsuranceReportId(insuranceReportId);
    } else {
      // Clear it when connecting to a conversation without insurance context
      setInsuranceReportId(null);
    }

    try {
      await wsManager.connect(conversationId, insuranceReportId);
      console.log(`Successfully connected to conversation: ${conversationId}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect to chat service';
      setError(errorMessage);
      setIsConnecting(false);
      isConnectingRef.current = false;
      console.error('Failed to connect WebSocket:', err);
      throw err;
    }
  }, []);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    wsManagerRef.current.disconnect();
    currentConversationIdRef.current = undefined;
    setInsuranceReportId(null);
  }, []);

  // Send message through WebSocket
  const sendMessage = useCallback((message: string, conversationId: string, attachmentIds?: string[]) => {
    const wsManager = wsManagerRef.current;
    const connected = wsManager.isConnected();
    console.log('WebSocket Context sendMessage - isConnected:', connected, 'isConnected state:', isConnected, 'isConnecting state:', isConnecting);
    
    if (!connected) {
      throw new Error('WebSocket is not connected');
    }
    wsManager.sendMessage(message, conversationId, attachmentIds);
  }, [isConnected, isConnecting]);

  // Subscribe to WebSocket messages
  const onMessage = useCallback((handler: (data: unknown) => void) => {
    return wsManagerRef.current.onMessage(handler);
  }, []);

  // Subscribe to title updates
  const onTitleUpdate = useCallback((callback: (data: { conversationId: string; title: string }) => void) => {
    return wsManagerRef.current.onTitleUpdate(callback);
  }, []);

  // Subscribe to conversation created events
  const onConversationCreated = useCallback((callback: (data: { conversation: Conversation }) => void) => {
    return wsManagerRef.current.onConversationCreated((data: { conversation: unknown }) => {
      // Type assertion since we know the structure from the API
      callback(data as { conversation: Conversation });
    });
  }, []);

  // Get current conversation ID that WebSocket is connected to
  const getCurrentConversationId = useCallback(() => {
    return wsManagerRef.current.getCurrentConversationId();
  }, []);

  // Set up WebSocket connection handler on mount
  useEffect(() => {
    const wsManager = wsManagerRef.current;
    const removeConnectionHandler = wsManager.onConnectionChange(handleConnectionChange);

    return () => {
      removeConnectionHandler();
      // Clean up on unmount
      wsManager.disconnect();
    };
  }, [handleConnectionChange]);

  const contextValue: WebSocketContextValue = {
    isConnected,
    isConnecting,
    error,
    insuranceReportId,
    setInsuranceReportId,
    getCurrentConversationId,
    connectToConversation,
    disconnect,
    sendMessage,
    onMessage,
    onTitleUpdate,
    onConversationCreated,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook to use the WebSocket context
export const useWebSocket = (): WebSocketContextValue => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
